/**
 * When two spellings name one place, as the database decides it.
 *
 * The unique index over `places` computes this in SQL (`backend/src/db/schema.ts`,
 * `placeIdentity`), and that is the authority: it is what makes «Ереван Сити» typed twice one
 * row. This is its twin in TypeScript, for the one caller that has to know the same thing
 * before any request is made — the screen that asks whether «Дописать в тот поход» would move
 * prices into *another* shop. Comparing the names as typed promised that damage for a trailing
 * space (MOL-25, В3).
 *
 * The two are held equal by an integration test that runs a corpus through Postgres and through
 * this function, for the reason `INVISIBLE` and `secret.ts` are held that way: a copy that
 * drifts from its twin is exactly the bug it was written to prevent.
 *
 * **It is not the search key's `nameIdentity`.** That one decides whether an *item* is a
 * duplicate and folds Armenian spellings on top of NFC; this one has to match a database index
 * character for character, so it is NFKC, drops variation selectors and trims the same list of
 * blanks — and, deliberately, does **not** collapse runs of spaces inside the name: `btrim`
 * touches the ends only, so «Ереван  Сити» with two spaces is a different place to the server
 * and must read as one here too.
 */

/**
 * What `btrim` is given in the index: the blanks a name may be padded with, invisible ones
 * included. Alternatives rather than a character class: a class holding a zero-width joiner is
 * read as one that can join what stands beside it, which is a real trap and a lint error here.
 */
const BLANKS = ['\u0020', '\t', '\r', '\n', '\u00A0', '\u200B', '\u200C', '\u200D', '\uFEFF'].join(
  '|',
)

/** Selectors choose how a character is drawn, never which character it is: «Кафе ☕» is one café. */
const SELECTORS = String.raw`\uFE00-\uFE0F\u{E0100}-\u{E01EF}`

const DRAWN = new RegExp(`[${SELECTORS}]`, 'gu')
const EDGES = new RegExp(`^(?:${BLANKS})+|(?:${BLANKS})+$`, 'gu')

/**
 * `lower()` in Postgres folds each character on its own, with none of the contextual rules
 * `String.prototype.toLowerCase` applies to a whole string — and the two disagreed on the first
 * day this file existed (Д3, Г-1). A final Greek sigma is the dangerous half: the database reads
 * «ΑΣ» and «Ας» as two places, JavaScript read them as one, and the screen would then keep quiet
 * about prices moving into another shop. So the fold is per character, which leaves «Σ» as «σ»
 * wherever it stands, and `İ` is named outright: its full mapping adds a combining dot that the
 * database's simple mapping does not.
 *
 * A test sweeps the whole Basic Multilingual Plane through both, so a third case cannot hide.
 */
const DOTTED_CAPITAL_I = '\u0130'

function lowerAsPostgresDoes(text: string): string {
  let folded = ''
  for (const character of text) {
    folded += character === DOTTED_CAPITAL_I ? 'i' : character.toLowerCase()
  }
  return folded
}

export function placeNameIdentity(name: string): string {
  return lowerAsPostgresDoes(name.normalize('NFKC').replace(DRAWN, '')).replace(EDGES, '')
}

/** Whether two names would reach the same row of `places`. */
export function isSamePlaceName(a: string, b: string): boolean {
  return placeNameIdentity(a) === placeNameIdentity(b)
}

/** A place as an answer names it: its name, and its city where the server sent one. */
export interface NamedPlace {
  readonly name: string
  readonly city?: string | undefined
}

/**
 * The city a place is printed with among these (MOL-120): only where its name stands in two
 * cities or more — «Ереван Сити» of Gyumri beside «Ереван Сити» of Yerevan — and nowhere else,
 * where it is noise. One rule for the queue of verdicts, a row of «Что брать» and a reminder,
 * each applying it to the set it shows — so they may differ over one shop, and on purpose
 * (owner's В-1): a reminder of three items names the Gyumri shop alone while «Оценки», holding
 * the Yerevan one as a fourth card, names it with its city (adversarial А5).
 *
 * Name and city are compared as the index over `places` compares them, so «SAS» of Gyumri and
 * «sas» of Yerevan repeat. A place with no city — an answer of the server before MOL-120, or one
 * the phone kept from it — could be either shop, so then no place of the set is given its city:
 * the set reads as it did before, and claims no two places where it cannot tell them apart.
 */
export function cityWhereNameRepeats(
  places: readonly NamedPlace[],
): (place: NamedPlace) => string | null {
  const cities = new Map<string, Set<string>>()
  for (const place of places) {
    if (place.city === undefined) return () => null
    const name = placeNameIdentity(place.name)
    const seen = cities.get(name) ?? new Set<string>()
    seen.add(placeNameIdentity(place.city))
    cities.set(name, seen)
  }
  return (place) =>
    (cities.get(placeNameIdentity(place.name))?.size ?? 0) > 1 ? (place.city ?? null) : null
}
